import { geohashForLocation } from "geofire-common";
import { hasDisplayableLocation } from "./locationQuality";

export const IP_LOCATION_TIMEOUT_MS = 1800;

export const IP_LOCATION_PROVIDERS = Object.freeze([
  {
    name: "ipwhois",
    url: "https://ipwho.is/",
    normalize(data = {}) {
      if (data.success === false) return null;
      return {
        ...data,
        ip: data.ip || "",
        version: data.type || "",
        city: data.city || "",
        region: data.region || "",
        region_code: data.region_code || "",
        postal: data.postal || "",
        country: data.country || "",
        country_name: data.country || "",
        country_code: data.country_code || "",
        continent_code: data.continent_code || "",
        in_eu: Boolean(data.is_eu),
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone?.id || "",
        utc_offset: data.timezone?.utc || "",
        country_calling_code: data.calling_code
          ? `+${String(data.calling_code).replace(/^\+/, "")}`
          : "",
        country_capital: data.capital || "",
        asn: data.connection?.asn ? `AS${data.connection.asn}` : "",
        org: data.connection?.org || data.connection?.isp || "",
        connection: data.connection || null,
      };
    },
  },
  {
    name: "ipapi",
    url: "https://ipapi.co/json/",
    normalize(data = {}) {
      return {
        ...data,
        ip: data.ip || "",
        city: data.city || "",
        region: data.region || "",
        region_code: data.region_code || "",
        postal: data.postal || "",
        country: data.country_name || data.country || "",
        country_code: data.country_code || "",
        latitude: data.latitude,
        longitude: data.longitude,
      };
    },
  },
]);

const getFiniteCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number !== 0 ? number : null;
};

const firstNonEmpty = (...values) =>
  values.find((value) => String(value ?? "").trim()) ?? "";

const normalizeCallingCode = (value = "") => {
  const cleaned = String(value || "").trim();
  if (!cleaned) return "";
  return cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
};

const normalizeStateCode = (...values) => {
  for (const value of values) {
    const cleaned = String(value || "")
      .trim()
      .toUpperCase();
    if (!cleaned) continue;
    if (/^[A-Z]{2}$/.test(cleaned)) return cleaned;

    const isoRegionMatch = cleaned.match(/^(?:US|USA)[\s_-]*([A-Z]{2})$/);
    if (isoRegionMatch) return isoRegionMatch[1];
  }
  return "";
};

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function fetchJsonWithTimeout(url, { fetchImpl, timeoutMs }) {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    const response = await fetchImpl(url, {
      signal: controller?.signal,
      cache: "no-store",
    });

    if (!response?.ok) {
      const error = new Error(
        `IP location provider returned ${response?.status}`,
      );
      error.status = response?.status;
      throw error;
    }

    return response.json();
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export function normalizeIpLocationData(rawLocation = {}, providerName = "") {
  const latitude = getFiniteCoordinate(
    rawLocation?.latitude ?? rawLocation?.lat,
  );
  const longitude = getFiniteCoordinate(
    rawLocation?.longitude ?? rawLocation?.lng,
  );

  if (latitude === null || longitude === null) return null;

  const timezone =
    typeof rawLocation?.timezone === "object"
      ? rawLocation.timezone?.id || ""
      : rawLocation?.timezone || "";
  const utcOffset =
    rawLocation?.utc_offset ||
    (typeof rawLocation?.timezone === "object"
      ? rawLocation.timezone?.utc || ""
      : "");
  const connection =
    rawLocation?.connection && typeof rawLocation.connection === "object"
      ? rawLocation.connection
      : {};
  const asn = firstNonEmpty(
    rawLocation?.asn,
    connection?.asn ? `AS${connection.asn}` : "",
  );
  const org = firstNonEmpty(rawLocation?.org, connection?.org, connection?.isp);
  const countryName = firstNonEmpty(
    rawLocation?.country_name,
    rawLocation?.country,
  );
  const countryCode = firstNonEmpty(
    rawLocation?.country_code,
    rawLocation?.countryCode,
  ).toUpperCase();
  const stateCode = normalizeStateCode(
    rawLocation?.stateCode,
    rawLocation?.region_code,
    rawLocation?.state_code,
  );
  const postal = firstNonEmpty(
    rawLocation?.postal,
    rawLocation?.zipCode,
    rawLocation?.zip,
  );
  const city = firstNonEmpty(rawLocation?.city, rawLocation?.locality);
  const region = firstNonEmpty(rawLocation?.region, rawLocation?.state);
  const version = firstNonEmpty(rawLocation?.version, rawLocation?.type);

  const location = {
    ...rawLocation,
    network: rawLocation?.network || "",
    version,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    city,
    locality: firstNonEmpty(rawLocation?.locality, city),
    region,
    state: firstNonEmpty(rawLocation?.state, region),
    region_code: stateCode,
    stateCode,
    postal,
    zipCode: firstNonEmpty(rawLocation?.zipCode, postal),
    country: firstNonEmpty(rawLocation?.country, countryName),
    country_name: countryName,
    country_code: countryCode,
    countryCode,
    country_code_iso3: rawLocation?.country_code_iso3 || "",
    country_capital: rawLocation?.country_capital || rawLocation?.capital || "",
    country_tld: rawLocation?.country_tld || "",
    continent_code: rawLocation?.continent_code || "",
    in_eu:
      typeof rawLocation?.in_eu === "boolean"
        ? rawLocation.in_eu
        : Boolean(rawLocation?.is_eu),
    timezone,
    utc_offset: utcOffset,
    country_calling_code: normalizeCallingCode(
      rawLocation?.country_calling_code || rawLocation?.calling_code,
    ),
    currency: rawLocation?.currency || "",
    currency_name: rawLocation?.currency_name || "",
    languages: rawLocation?.languages || "",
    country_area: rawLocation?.country_area ?? null,
    country_population: rawLocation?.country_population ?? null,
    asn,
    org,
    geohash: geohashForLocation([latitude, longitude]),
    locationSource: "ip",
    locationProvider: providerName,
  };

  return hasDisplayableLocation(location) ? location : null;
}

export async function getIpLocation({
  fetchImpl = typeof fetch === "function" ? fetch : null,
  providers = IP_LOCATION_PROVIDERS,
  timeoutMs = IP_LOCATION_TIMEOUT_MS,
  retryDelayMs = 150,
} = {}) {
  if (!fetchImpl) return null;

  for (let index = 0; index < providers.length; index += 1) {
    const provider = providers[index];

    try {
      const data = await fetchJsonWithTimeout(provider.url, {
        fetchImpl,
        timeoutMs,
      });
      const providerLocation =
        typeof provider.normalize === "function"
          ? provider.normalize(data)
          : data;
      const location = normalizeIpLocationData(
        providerLocation,
        provider.name || `provider-${index + 1}`,
      );

      if (location) return location;
    } catch (error) {
      if (process.env.NODE_ENV !== "test") {
        console.warn("IP location provider failed.", {
          provider: provider.name || provider.url,
          status: error?.status || null,
          message: error?.message || String(error),
        });
      }
    }

    if (retryDelayMs > 0 && index < providers.length - 1) {
      await wait(retryDelayMs);
    }
  }

  return null;
}
